import Button from "@/lib/Button";
import { useState } from "react";
import toast from "react-hot-toast";
import { LocalLineItems } from "./types";
import LineItemList from "./LineItemList";
import EditLineItemsForm from "./EditLineItemsForm";
import QuickSelectLineItems from "./QuickSelectLineItems";
import useToPage from "@/lib/useToPage";
import { components } from "@/lib/ApiSchema";
import { useParams } from "react-router";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import CustomerInfo from "./CustomerInfo";
import Note from "./Note";
import useGetLineItems from "./useGetLineItems";
import useResetLineItems from "./useResetLineItems";
import { useQueryClient } from "@tanstack/react-query";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import { ListCard } from "@/lib/Card";
import $api from "@/lib/queries/openapi";

export type UpdateLineItemsParams = {
  lineItems: LocalLineItems[];
  save: boolean;
};

export default function Quote() {
  const { toSend } = useToPage();
  const { teamSlug } = useParams();
  const quoteRevision = useGetCurrentQuoteRevision();
  const canEditQuoteRevision = quoteRevision?.can_edit;
  const [localLineItems, setLocalLineItems] = useState<LocalLineItems[]>();
  const queryClient = useQueryClient();

  const apiLineItems = useGetLineItems(quoteRevision?.id, teamSlug);
  const { mutate: saveLineItems, isPending } = $api.useMutation(
    "put",
    "/api/teams/{team_slug}/quote_revisions/{id}/line_items",
  );
  const { mutate: confirmLineItems, isPending: pendingConfirm } =
    $api.useMutation(
      "post",
      "/api/teams/{team_slug}/quote_revisions/{id}/confirm_line_items",
    );

  const resetLineItems = useResetLineItems(
    quoteRevision?.id,
    teamSlug,
    () => {
      setLocalLineItems(undefined);
      toast.success("Line items reset");
    },
    () => toast.error("Failed to reset line items"),
  );

  const lineItems = localLineItems ?? fromApiLineItems(apiLineItems ?? []);
  const [selectedLineItemKeys, setSelectedLineItemKeys] = useState<string[]>(
    [],
  );
  const [customerInfoValid, setCustomerInfoValid] = useState(false);

  const { data: shop } = useGetShopSettings(teamSlug);
  const hasAccountingSystem = !!shop?.accounting_system_name;

  if (!quoteRevision) return null;

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "get",
        "/api/teams/{team_slug}/quote_revisions/{id}/line_items",
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "get",
        "/api/teams/{team_slug}/quote_revisions/{id}",
        {
          params: {
            path: {
              team_slug: teamSlug!,
              id: quoteRevision?.id,
            },
          },
        },
      ],
    });
  };

  return (
    // -61px to cover up for the sticky footer below
    <div className="h-[calc(100%-61px)]">
      <div className="h-full flex divide-x">
        <div className="grow flex flex-col px-2 pb-4">
          <div className="flex gap-2 justify-between items-center py-2">
            <h2 className="text-2xl font-bold">Quote</h2>
            {canEditQuoteRevision && (
              <div className="flex gap-4">
                <Button
                  onClick={() =>
                    updateLineItems({
                      lineItems: roundUnitPrice(lineItems),
                      save: true,
                    })
                  }
                  loading={isPending}
                >
                  Round Unit Price
                </Button>
                <Button onClick={resetLineItems}>Reset</Button>
              </div>
            )}
          </div>
          <ListCard>
            <LineItemList
              selectedLineItemKeys={selectedLineItemKeys}
              setSelectedLineItemKeys={setSelectedLineItemKeys}
              lineItems={lineItems}
              deleteLineItem={(lineItemKey: string) =>
                updateLineItems({
                  lineItems: deleteLineItem(lineItems, lineItemKey),
                  save: true,
                })
              }
              updateLineItems={updateLineItems}
              clearLocalLineItems={() => setLocalLineItems(undefined)}
              disabled={!canEditQuoteRevision}
            />
          </ListCard>
        </div>
        <div className="w-lg flex flex-col divide-y bg-white">
          {canEditQuoteRevision && (
            <>
              {selectedLineItemKeys.length === 0 ? (
                <QuickSelectLineItems
                  lineItems={lineItems}
                  setSelectedLineItemKeys={setSelectedLineItemKeys}
                />
              ) : (
                <EditLineItemsForm
                  lineItems={lineItems}
                  updateLineItems={updateLineItems}
                  selectedLineItemKeys={selectedLineItemKeys}
                  setSelectedLineItemKeys={setSelectedLineItemKeys}
                />
              )}
            </>
          )}
          <CustomerInfo onValidChange={setCustomerInfoValid} />
          <Note />
        </div>
      </div>
      {canEditQuoteRevision && (
        <div className="sticky bottom-0 w-full bg-white flex gap-2 justify-end items-center p-3 z-10 border-t">
          <Button
            primary
            disabled={!customerInfoValid || pendingConfirm}
            loading={pendingConfirm}
            onClick={() =>
              confirmLineItems(
                {
                  params: {
                    path: {
                      team_slug: teamSlug!,
                      id: quoteRevision.id,
                    },
                  },
                },
                {
                  onSuccess: () => {
                    invalidateQueries();
                    toSend();
                  },
                },
              )
            }
          >
            Preview Quote{hasAccountingSystem && " and Update QB"}
          </Button>
        </div>
      )}
    </div>
  );

  function updateLineItems({ lineItems, save }: UpdateLineItemsParams) {
    setLocalLineItems(lineItems);
    if (save) {
      saveLineItems(
        {
          params: {
            path: {
              team_slug: teamSlug!,
              id: quoteRevision!.id,
            },
          },
          body: toApiLineItems(lineItems),
        },
        {
          onSuccess: () => invalidateQueries(),
        },
      );
    }
  }
}

function toApiLineItems(lineItems: LocalLineItems[]) {
  return lineItems.map((part) => ({
    requested_part_revision_id: part.requested_part_revision_id,
    item_lines: part.item_lines.map(
      ({ unitPrice: unit_price, lead_time, quantity }) => ({
        quantity,
        lead_time,
        unit_price: unit_price.toFixed(2),
      }),
    ),
  }));
}

function fromApiLineItems(
  apiLineItems: components["schemas"]["LineItemSchema"][],
) {
  return apiLineItems.map((part) => ({
    ...part,
    item_lines: part.item_lines.map((l, i) => ({
      ...l,
      unitPrice: parseFloat(l.unit_price.toString()),
      key: itemLineKey(part.requested_part_revision_id, i),
      highlight: false,
    })),
  }));
}

function itemLineKey(requested_part_revision_id: string, i: number) {
  return `${requested_part_revision_id}~${i}`;
}

function roundUnitPrice(lineItems: LocalLineItems[]) {
  return lineItems.map((p) => ({
    ...p,
    item_lines: p.item_lines.map((l) => ({
      ...l,
      unitPrice: Math.round(l.unitPrice),
    })),
  }));
}

function deleteLineItem(parts: LocalLineItems[], lineItemKey: string) {
  return parts
    .map((p) => ({
      ...p,
      item_lines: p.item_lines.filter((l) => l.key !== lineItemKey),
    }))
    .filter((p) => p.item_lines.length > 0);
}
